import {
  ErrorMessages,
  InteriorComponent,
  ProductInfoComponent,
  ProductType
} from '@inter-ikea-storageone/range';
import { BehaviorParameters } from 'rangeConfig/@types';
import { hideNoFramesInSceneErrorMessage } from 'rangeConfig/shared/rules/hideMenuRules';
import replaceShelfWithCabinet from './replaceShelfWithCabinet';
import { CommunicationSizeComponent, ComponentIds, Entity } from '@inter-ikea-core/core';

const addProduct = ({ coreApi, props, rootState, dispatch }: BehaviorParameters) => {
  const productId = props.coreProductIds ? props.coreProductIds[0] : props.coreProductId;
  const product = rootState.products.inMarket.find(p => p.coreProductId === productId);
  if (!product) return;

  // Handle adding of sections separately from interiors
  if (product.type === ProductType.LADMAKARE_SECTION_WITH_SHELVES) {
    coreApi.products.add(productId, props.position);
    return;
  }

  const framesInScene = coreApi.entities.getByComponent(ComponentIds.FRAME_COMPONENT_ID);
  const wideFramesInScene = framesInScene.filter(
    f =>
      f.getComponent<CommunicationSizeComponent>(ComponentIds.COMMUNICATION_SIZE_COMPONENT_ID)?.size
        ?.width === 800
  );

  const onlySmallFramesInScene = framesInScene.length > 0 && wideFramesInScene.length === 0;

  const interiorAtPosition =
    props.position !== undefined
      ? coreApi.entities.getEntityClosestToPosition(props.position, [InteriorComponent.id])
      : null;

  const addingInteriorWillFail =
    onlySmallFramesInScene ||
    (interiorAtPosition &&
      interiorAtPosition?.getComponent<CommunicationSizeComponent>(
        ComponentIds.COMMUNICATION_SIZE_COMPONENT_ID
      )?.size?.width !== 800);

  if (product.type === ProductType.LADMAKARE_CABINET && !addingInteriorWillFail) {
    const cabinet = coreApi.getServices().productManager.createEntity(productId);
    if (!cabinet) return;

    // When adding a cabinet we must find a suitable spot ourselves
    // That takes into account adjacent cabinets, top of section and more
    // This fn will iterate over shelves in scene until a suitable place is found

    let shelves: Entity[] = [];
    if (interiorAtPosition) {
      // User might have dragged to position of where bottom of cabinet is expected
      // Which might fail if something is blocked the hole under
      // Therefore we also check next interior to handle that scenario
      const interiorAtPositionAbove = coreApi.entities.getEntityClosestToPosition(
        { x: props.position.x, y: props.position.y + 450 },
        [InteriorComponent.id]
      );

      const interiors = [interiorAtPosition, interiorAtPositionAbove].filter(
        i =>
          i !== null &&
          i.getComponent<ProductInfoComponent>(ProductInfoComponent.id)?.productType ===
            ProductType.LADMAKARE_SHELF
      ) as Entity[];
      shelves = interiors;
    } else {
      shelves = coreApi.entities.getByProductType(ProductType.LADMAKARE_SHELF);
    }
    if (shelves.length === 0) {
      coreApi.emit.onPlacementAttempt(ErrorMessages.ItemWontFit);
      return;
    }
    let success = false;
    for (const entity of shelves) {
      const res = replaceShelfWithCabinet({
        coreApi,
        props,
        rootState,
        dispatch,
        entity
      });
      if (res?.valid) {
        success = true;
        break;
      }
    }

    if (!success) {
      const placementResult = coreApi.products.addToTarget(productId, shelves[0]);
      if (!placementResult.valid && placementResult.errors && placementResult.errors.length > 0) {
        coreApi.emit.onPlacementAttempt(ErrorMessages.ItemWontFit);
      }
    }

    return;
  }

  let placementResult;
  if (addingInteriorWillFail && interiorAtPosition) {
    // This add will fail but we trigger it to show the error message
    placementResult = coreApi.products.addToTarget(productId, interiorAtPosition);
  } else {
    placementResult = coreApi.products.add(productId, props.position);
  }

  if (!placementResult.valid && placementResult.errors && placementResult.errors.length > 0) {
    const determinePlacementAttemptErrorMessage = () => {
      if (!hideNoFramesInSceneErrorMessage(coreApi)) {
        return ErrorMessages.NoFramesInScene;
      } else if (addingInteriorWillFail) {
        return ErrorMessages.LadmakareInteriorOnWideFramesOnly;
      } else {
        return ErrorMessages.ItemWontFit;
      }
    };
    const placementAttemptErrorMessage = determinePlacementAttemptErrorMessage();
    coreApi.emit.onPlacementAttempt(placementAttemptErrorMessage);
    coreApi.entities.disposeCollidingEntities(placementResult.errors);
  }
  return placementResult;
};

export default addProduct;
